import { ProductLevel } from 'permissions/constants/ProductLevels';
import HelpIcon from '@mui/icons-material/HelpOutlineOutlined';
import { ReactComponent as DashboardIcon } from 'images/icons/Material/dashboard.svg';
import { ReactComponent as CampaignIcon } from 'images/icons/Material/campaign.svg';
import { ReactComponent as SchoolIcon } from 'images/icons/Material/school.svg';
import { ReactComponent as AgentIcon } from 'images/icons/Material/support_agent.svg';
import { ReactComponent as SettingsIcon } from 'images/icons/Material/settings.svg';
import { Permission, UserRole } from 'permissions/constants/Roles';
import { getConfig } from 'utils/config';

const { marketplaceUrl } = getConfig();

export const routes = [
  {
    title: 'Workflows',
    url: 'management/workflows',
    mainNav: true,
    allowedFor: 'role',
    allowedRoleTypes: [UserRole.EnrollerAdmin],
    mobileIcon: DashboardIcon,
    requiresSchool: false,
  },
  {
    title: 'Dashboard',
    url: 'dashboard',
    mainNav: true,
    allowedFor: 'permission',
    allowedRoleTypes: [Permission.ViewDashboard],
    mobileIcon: DashboardIcon,
    disallowedProducts: [ProductLevel.EnrollerRecruit],
    requiresSchool: true,
  },
  {
    title: 'Marketplace',
    url: marketplaceUrl,
    mainNav: true,
    external: true,
    allowedFor: 'role',
    allowedRoleTypes: [UserRole.InstitutionalStaff, UserRole.InstitutionalAdmin, UserRole.Agent, UserRole.InstitutionalMarketingOnlyStaff],
    mobileIcon: CampaignIcon,
    requiresSchool: true,
  },
  {
    title: 'Enrolments',
    url: '/enrolments',
    mainNav: true,
    allowedFor: 'role',
    allowedRoleTypes: [UserRole.Agent],
    mobileIcon: DashboardIcon,
    requiresSchool: true,
  },
  {
    title: 'Shared Enrolments',
    url: '/shared-enrolments',
    mainNav: true,
    allowedFor: 'role',
    allowedRoleTypes: [UserRole.SharedApplicant],
    mobileIcon: DashboardIcon,
  },
  {
    title: 'Students',
    url: 'studentsummary',
    mainNav: true,
    allowedFor: 'role',
    allowedRoleTypes: [UserRole.InstitutionalStaff, UserRole.InstitutionalAdmin, UserRole.InstitutionalReadOnlyStaff],
    mobileIcon: SchoolIcon,
    requiresSchool: true,
    children: [
      {
        label: 'Enquiries',
        url: 'enquiries',
        allowedFor: 'permission',
        allowedRoleTypes: [Permission.ViewLead],
      },
      {
        label: 'Enrolments',
        url: 'pipeline',
        allowedFor: 'role',
        allowedRoleTypes: [UserRole.InstitutionalStaff, UserRole.InstitutionalAdmin, UserRole.InstitutionalReadOnlyStaff],
      },
      {
        label: 'Awaiting arrival',
        url: 'studentsummary?studentStatus=Impending',
        allowedFor: 'role',
        allowedRoleTypes: [UserRole.InstitutionalStaff, UserRole.InstitutionalAdmin, UserRole.InstitutionalReadOnlyStaff],
        disallowedProducts: [ProductLevel.EnrollerRecruit],
      },
      {
        label: 'Current students',
        url: 'studentsummary?studentStatus=Current',
        allowedFor: 'role',
        allowedRoleTypes: [UserRole.InstitutionalStaff, UserRole.InstitutionalAdmin, UserRole.InstitutionalReadOnlyStaff],
        disallowedProducts: [ProductLevel.EnrollerRecruit],
      },
      {
        label: 'Alumni students',
        url: 'studentsummary?studentStatus=Alumni',
        allowedFor: 'role',
        allowedRoleTypes: [UserRole.InstitutionalStaff, UserRole.InstitutionalAdmin, UserRole.InstitutionalReadOnlyStaff],
        disallowedProducts: [ProductLevel.EnrollerRecruit],
      },
      {
        label: 'All enrolled students',
        url: 'studentsummary?studentStatus=All',
        allowedFor: 'role',
        allowedRoleTypes: [UserRole.InstitutionalStaff, UserRole.InstitutionalAdmin, UserRole.InstitutionalReadOnlyStaff],
        disallowedProducts: [ProductLevel.EnrollerRecruit],
      },
      {
        label: 'Archived students',
        url: 'studentsummary?studentStatus=Archived',
        allowedFor: 'role',
        allowedRoleTypes: [UserRole.InstitutionalStaff, UserRole.InstitutionalAdmin, UserRole.InstitutionalReadOnlyStaff],
        allowedProducts: [ProductLevel.EnrollerRecruit],
      },
    ],
  },
  {
    title: 'Enrolment',
    url: 'pipeline',
    mainNav: true,
    allowedFor: 'role',
    allowedRoleTypes: [UserRole.Applicant, UserRole.Parent],
    mobileIcon: SchoolIcon,
    requiresSchool: true,
  },
  {
    title: 'Agents',
    url: 'settings/agents',
    mainNav: true,
    allowedFor: 'permission',
    allowedRoleTypes: [Permission.ViewInstitutionAgents],
    mobileIcon: AgentIcon,
    requiresSchool: true,
  },
  {
    title: 'Create a new lead',
    url: 'leads/add',
    mainNav: false,
  },
  {
    title: 'Edit lead details',
    url: 'leads/edit/:id',
    mainNav: false,
  },
  {
    title: 'Settings',
    url: 'settings',
    mainNav: true,
    hide: true,
    children: [
      {
        label: 'User Settings',
        url: 'settings/permissions',
        allowedFor: 'permission',
        allowedRoleTypes: [Permission.ViewInstitutionUsers],
        disallowedProducts: [ProductLevel.EnrollerRecruit],
      },
      {
        label: 'School Settings',
        url: 'settings/school',
        allowedFor: 'permission',
        allowedRoleTypes: [Permission.ViewInstitutionSettings],
      },
      {
        label: 'Application Settings',
        url: 'settings/application',
        allowedFor: 'permission',
        allowedRoleTypes: [Permission.ViewApplicationSettings],
      },
      {
        label: 'Financial Settings',
        url: 'settings/financial',
        allowedFor: 'permission',
        allowedRoleTypes: [Permission.ViewFinancialSettings],
        disallowedProducts: [ProductLevel.EnrollerRecruit],
      },
    ],
    allowedFor: 'role',
    allowedRoleTypes: [UserRole.InstitutionalAdmin],
    mobileIcon: SettingsIcon,
  },
  {
    title: 'Help Center',
    titleIcon: HelpIcon,
    mobileIcon: HelpIcon,
    url: 'https://help.enroller.co.nz/',
    mainNav: true,
    external: true,
    alignRight: true,
    allowedFor: 'role',
    allowedRoleTypes: [
      UserRole.Applicant,
      UserRole.Agent,
      UserRole.Parent,
      UserRole.InstitutionalReadOnlyStaff,
      UserRole.InstitutionalStaff,
      UserRole.InstitutionalAdmin,
    ],
  },
  {
    title: 'Profile',
    url: '/profile',
    mainNav: true,
    hideOnMobile: true,
    alignRight: true,
    allowedFor: 'role',
    allowedRoleTypes: [
      UserRole.EnrollerAdmin,
      UserRole.Applicant,
      UserRole.Agent,
      UserRole.Parent,
      UserRole.InstitutionalReadOnlyStaff,
      UserRole.InstitutionalMarketingOnlyStaff,
      UserRole.InstitutionalStaff,
      UserRole.InstitutionalAdmin,
    ],
  },
];

export const mainNav = routes.filter((route) => route.mainNav);
